import _extends from '@babel/runtime/helpers/extends';
import React__default, { forwardRef, useRef, useEffect, useCallback } from 'react';
import styled, { css } from 'styled-components';
import { u as useIsMobile } from '../theme/MobileThemeProvider/useIsMobile-B-VP6bHU.js';
import { s as srOnlyMixin } from '../theme/style-mixins/srOnly-D3ByQyko.js';
import { f as focusOutline } from '../theme/style-mixins/FocusIndicator/focusOutline-oDdaDZ4N.js';
import { C as CheckboxIcon, h as hoverMixin, a as activeMixin } from './CheckboxIcon-CUJabl3A.js';
import { n as noop } from '../LocalModules/storm-ui-utils/dist/esm/noop-BNkHXPFl-BNkHXPFl.js';
import { K as KEYBOARD_KEYNAMES } from '../LocalModules/storm-ui-utils/dist/esm/keyboardKeynames-CVa_vjg3-CVa_vjg3.js';
import { a as TaktIdConsumer } from '../TaktIdContext/TaktIdContext-C6vdL-6Z.js';
import { c as createStormTaktId } from '../TaktIdContext/TaktIdContext.utils-B0CzqlN8.js';
import { c as composeRefs } from '../LocalModules/storm-ui-utils/dist/esm/composeRefs-CHPW6wJ8-CHPW6wJ8.js';
import { T as Text } from '../Text/Text-CO4HftGt.js';

const StyledCheckboxIcon = /*#__PURE__*/styled(CheckboxIcon).withConfig({
  componentId: "sc-storm-ui-40026394__sc-1gskqay-0"
})(["", ""], ({
  size,
  $positionStart
}) => css(["", ";"], $positionStart && css(["margin-top:", ""], size === 'base' ? '3px' : '4px')));
const checkboxContainerStyles = /*#__PURE__*/css(["display:flex;flex-wrap:nowrap;cursor:pointer;align-items:", ";padding:0;", " ", ""], ({
  $positionStart
}) => $positionStart ? 'start' : 'center', ({
  $disabled
}) => !$disabled && css([":hover{&& > ", "{", "}}:active{&& > ", "{transform:scale(1.2);", "}}"], StyledCheckboxIcon, hoverMixin, StyledCheckboxIcon, activeMixin), ({
  $disabled
}) => $disabled && css(["cursor:not-allowed;outline:none;"]));
const CheckboxLabelContainer = /*#__PURE__*/styled.label.withConfig({
  componentId: "sc-storm-ui-40026394__sc-1gskqay-1"
})(["", ""], checkboxContainerStyles);
const CheckboxDummyContainer = /*#__PURE__*/styled.div.withConfig({
  componentId: "sc-storm-ui-40026394__sc-1gskqay-2"
})(["", ""], checkboxContainerStyles);
const HiddenCheckbox = /*#__PURE__*/styled.input.withConfig({
  componentId: "sc-storm-ui-40026394__sc-1gskqay-3"
})(["&&:focus-visible{outline:none;}&&{", "}"], srOnlyMixin);

// this is to fix https://sim.amazon.com/issues/CFX-3552
const HiddenWrapper = /*#__PURE__*/styled.span.withConfig({
  componentId: "sc-storm-ui-40026394__sc-1gskqay-4"
})(["position:relative;&&:has(:focus-visible) + ", "{", "}"], StyledCheckboxIcon, focusOutline);
const LabelWrapper = /*#__PURE__*/styled(Text).withConfig({
  componentId: "sc-storm-ui-40026394__sc-1gskqay-5"
})(["margin-inline-start:", ";", " ", ""], ({
  theme,
  isMobile
}) => theme.selectableWidget.controlLabelGap[isMobile ? 'mobile' : 'default'], ({
  $hideLabel
}) => $hideLabel && srOnlyMixin, ({
  theme,
  $size
}) => $size && css(["font:", ";"], $size === 'base' ? theme.font.text.base.default : theme.font.text.medium.default));
const CheckboxContainer = ({
  withoutInput,
  ...restProps
}) => {
  if (withoutInput) {
    return /*#__PURE__*/React__default.createElement(CheckboxDummyContainer, restProps);
  }
  return /*#__PURE__*/React__default.createElement(CheckboxLabelContainer, restProps);
};
const Checkbox = /*#__PURE__*/forwardRef(({
  disabled = false,
  hideLabel = false,
  id,
  name,
  indeterminate = false,
  label,
  positionWithLabel = 'center',
  checked = false,
  className,
  size,
  taktId,
  taktValue,
  onChange = noop,
  withoutInput = false,
  ...rest
}, checkboxRef) => {
  const internalInputRef = useRef(null);
  const isMobile = useIsMobile();
  const positionStart = !hideLabel && positionWithLabel === 'start';
  useEffect(() => {
    if (internalInputRef.current !== null) {
      // eslint-disable-next-line no-param-reassign
      internalInputRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate, checked]);
  const handleChange = useCallback(() => {
    var _internalInputRef$cur;
    if (withoutInput) {
      onChange === null || onChange === void 0 || onChange({
        target: {
          checked: !checked
        }
      });
      return;
    }
    // create a custom event for the onChange
    const inputEvent = new CustomEvent('change', {
      bubbles: false,
      cancelable: true
    });
    // define target to be the actual input element, with updated checked value
    Object.defineProperty(inputEvent, 'target', {
      value: {
        ...internalInputRef.current,
        checked: !checked
      },
      writable: true,
      enumerable: true,
      configurable: true
    });
    // Dispatch the change event on the input element
    onChange === null || onChange === void 0 || onChange(inputEvent);
    internalInputRef === null || internalInputRef === void 0 || (_internalInputRef$cur = internalInputRef.current) === null || _internalInputRef$cur === void 0 || _internalInputRef$cur.focus();
  }, [withoutInput, checked, onChange]);
  const handleKeyPress = useCallback(event => {
    if (event.key === KEYBOARD_KEYNAMES.SPACE) {
      handleChange();
    }
  }, [handleChange]);
  return /*#__PURE__*/React__default.createElement(TaktIdConsumer, {
    taktId: taktId,
    taktValue: taktValue,
    fallbackId: createStormTaktId('checkbox')
  }, ({
    getDataTaktAttributes
  }) => {
    var _rest$ariaChecked;
    return /*#__PURE__*/React__default.createElement(CheckboxContainer, _extends({
      withoutInput: withoutInput
    }, withoutInput && {
      onKeyDown: handleKeyPress,
      onClick: handleChange
    }, {
      className: className,
      $disabled: disabled,
      $positionStart: positionStart,
      $indeterminate: indeterminate,
      $checked: checked,
      htmlFor: id
    }, getDataTaktAttributes()), !withoutInput && /*#__PURE__*/React__default.createElement(HiddenWrapper, null, /*#__PURE__*/React__default.createElement(HiddenCheckbox, _extends({
      type: "checkbox",
      id: id,
      name: name,
      checked: checked,
      "aria-checked": (_rest$ariaChecked = rest['aria-checked']) !== null && _rest$ariaChecked !== void 0 ? _rest$ariaChecked : checked,
      disabled: disabled,
      "aria-disabled": disabled,
      onChange: onChange,
      ref: composeRefs(internalInputRef, checkboxRef)
    }, getDataTaktAttributes(), rest))), /*#__PURE__*/React__default.createElement(StyledCheckboxIcon, {
      role: withoutInput ? 'checkbox' : undefined,
      size: size !== null && size !== void 0 ? size : isMobile ? 'large' : 'base',
      checked: checked,
      indeterminate: indeterminate,
      disabled: disabled,
      $positionStart: positionStart
    }), label && /*#__PURE__*/React__default.createElement(LabelWrapper, _extends({
      isMobile: isMobile,
      renderAs: "span",
      $hideLabel: hideLabel,
      $size: size
    }, getDataTaktAttributes()), typeof label === 'string' ? /*#__PURE__*/React__default.createElement(Text, {
      inline: true,
      color: disabled ? 'unavailable' : 'default',
      size: "inherit"
    }, label) : label));
  });
});

export { Checkbox as C };
//# sourceMappingURL=Checkbox-D3FAynj4.js.map
